import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    bulkClearLicenseTseIssueActive,
    checkFiskalySignApiGlobalHealth,
    getAllLicensesByResellerId,
    insertLicense,
    updateLicense,
} from "../../../actions/licensesActions";
import "./Licenses.scss";

function Licenses() {
    const [licenses, setLicenses] = useState([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newLicense, setNewLicense] = useState({
        StoreName: "",
        Owner: "",
        ValidityDate: new Date().toISOString().split("T")[0],
    });
    const [globalFiskalyOk, setGlobalFiskalyOk] = useState(false);
    const [globalCheckLoading, setGlobalCheckLoading] = useState(false);
    const [globalCheckDetail, setGlobalCheckDetail] = useState("");
    const [bulkAckLoading, setBulkAckLoading] = useState(false);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const identity = useSelector((state) => state.identity);

    const licenseIdsWithTseIssue = useMemo(() => {
        return licenses
            .filter((l) => l.tseIssueActive === true)
            .map((l) => Number(l.LicenseId))
            .filter((id) => Number.isInteger(id) && id > 0);
    }, [licenses]);

    const showGlobalTseToolbar = licenseIdsWithTseIssue.length > 1;

    useEffect(() => {
        loadLicenses();
    }, []);

    const loadLicenses = () => {
        dispatch(getAllLicensesByResellerId(identity.user.resellerId)).then((json) => {
            // const treeData = createTree(json);
            setLicenses(json);
            setGlobalFiskalyOk(false);
            setGlobalCheckDetail("");
        });
    };

    const handleGlobalFiskalyCheck = () => {
        setGlobalCheckLoading(true);
        setGlobalFiskalyOk(false);
        setGlobalCheckDetail("");
        dispatch(checkFiskalySignApiGlobalHealth())
            .then((res) => {
                if (res && res.ok === true) {
                    setGlobalFiskalyOk(true);
                    setGlobalCheckDetail(res.detail || "Sign-API erreichbar.");
                } else {
                    setGlobalFiskalyOk(false);
                    setGlobalCheckDetail(res?.detail || "Sign-API nicht erreichbar oder nicht konfiguriert.");
                }
            })
            .catch(() => {
                setGlobalFiskalyOk(false);
                setGlobalCheckDetail("Anfrage fehlgeschlagen.");
            })
            .finally(() => setGlobalCheckLoading(false));
    };

    const handleBulkTseAckConfirm = () => {
        if (!globalFiskalyOk || licenseIdsWithTseIssue.length === 0) {
            setBulkConfirmOpen(false);
            return;
        }
        setBulkAckLoading(true);
        dispatch(bulkClearLicenseTseIssueActive(licenseIdsWithTseIssue))
            .then((res) => {
                setBulkConfirmOpen(false);
                if (res && res.success === true) {
                    setSnackbar({
                        open: true,
                        message: `TSE-Hinweise quittiert (${res.updatedCount ?? licenseIdsWithTseIssue.length} Lizenzen).`,
                        severity: "success",
                    });
                    loadLicenses();
                } else {
                    setSnackbar({ open: true, message: "Quittieren fehlgeschlagen.", severity: "error" });
                }
            })
            .catch(() => {
                setSnackbar({ open: true, message: "Quittieren fehlgeschlagen.", severity: "error" });
            })
            .finally(() => setBulkAckLoading(false));
    };

    const handleUpdate = (updatedRow) => {
        dispatch(updateLicense(updatedRow)).then(() => loadLicenses());
    };

    const handleInsert = () => {
        const dataObject = {
            ...newLicense,
            resellerId: identity.user.resellerId,
        };
        dispatch(insertLicense(dataObject)).then(() => {
            loadLicenses();
            setIsAddDialogOpen(false);
            setNewLicense({
                StoreName: "",
                Owner: "",
                ValidityDate: new Date().toISOString().split("T")[0],
            });
        });
    };

    const columns = [
        {
            field: "StoreName",
            headerName: "Name",
            flex: 1,
            editable: true,
            renderCell: (params) => <span style={{ paddingLeft: params.row.level * 20 }}>{params.value}</span>,
        },
        { field: "Owner", headerName: "Inhaber / GF", flex: 1, editable: true },
        {
            field: "ValidityDate",
            headerName: "Lizenzablauf",
            flex: 1,
            editable: true,
            type: "date",
            valueGetter: (value) => new Date(value),
        },
        { field: "LicenseNumber", headerName: "Lizenznummer", flex: 1 },
        { field: "deployFrontendUrl", headerName: "Frontend URL", flex: 1 },
        {
            field: "tseIssueActive",
            headerName: "TSE-Warnung",
            flex: 0.7,
            editable: false,
            align: "center",
            headerAlign: "center",
            cellClassName: "licenses-tse-warning-cell",
            // MUI X v7: valueGetter(value, row, …) — first arg is field value
            valueGetter: (value) => Boolean(value),
            renderCell: (params) => {
                const active = params.row?.tseIssueActive === true;
                const cellCenterSx = {
                    width: "100%",
                    height: "100%",
                    minHeight: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                };
                if (active) {
                    return (
                        <Box sx={cellCenterSx}>
                            <Tooltip title="TSE-Hinweis aktiv – Lizenz öffnen und quittieren">
                                <Box
                                    component="span"
                                    aria-label="TSE-Fehler"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "warning.main",
                                    }}
                                >
                                    <WarningAmberIcon sx={{ fontSize: 28 }} aria-hidden="true" />
                                </Box>
                            </Tooltip>
                        </Box>
                    );
                }
                return (
                    <Box sx={cellCenterSx}>
                        <Typography variant="body2" color="text.disabled" component="span">
                            —
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Aktionen",
            width: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => navigate(`/lizenzen/${id}`)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    const createTree = (licenses, parentId = null, level = 0, parentPath = []) => {
        return licenses
            .filter((license) => license.parentLicenseId === parentId)
            .map((license) => {
                const path = [...parentPath, license.LicenseId];
                return {
                    ...license,
                    level,
                    path,
                    children: createTree(licenses, license.LicenseId, level + 1, path),
                };
            });
    };

    return (
        <div className="licenses">
            <Typography variant="h4" gutterBottom sx={{ padding: 2, mb: 2 }}>
                Lizenzen
            </Typography>
            {showGlobalTseToolbar && (
                <Box sx={{ px: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Mehrere aktive TSE-Warnungen
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                        <Button
                            variant="outlined"
                            onClick={handleGlobalFiskalyCheck}
                            disabled={globalCheckLoading}
                            data-testid="licenses-global-fiskaly-check"
                        >
                            Fiskaly Sign-API (global) prüfen
                        </Button>
                        {globalCheckLoading && <CircularProgress size={24} aria-label="Prüfung läuft" />}
                        {globalCheckDetail ? (
                            <Typography
                                variant="body2"
                                color={globalFiskalyOk ? "success.main" : "error.main"}
                                sx={{ maxWidth: 480 }}
                            >
                                {globalCheckDetail}
                            </Typography>
                        ) : null}
                        <Button
                            variant="contained"
                            color="warning"
                            disabled={!globalFiskalyOk || bulkAckLoading}
                            onClick={() => setBulkConfirmOpen(true)}
                            data-testid="licenses-bulk-tse-ack"
                        >
                            Alle TSE-Hinweise quittieren
                        </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Massen-Quittieren ist erst nach erfolgreicher globaler Erreichbarkeitsprüfung der Fiskaly Sign-API
                        möglich (betrifft alle Lizenzen mit aktiver TSE-Warnung in dieser Liste).
                    </Typography>
                </Box>
            )}
            <Dialog open={bulkConfirmOpen} onClose={() => !bulkAckLoading && setBulkConfirmOpen(false)}>
                <DialogTitle>Alle TSE-Hinweise quittieren?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Es werden {licenseIdsWithTseIssue.length} Lizenzen mit aktiver TSE-Warnung quittiert. Fortfahren?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkConfirmOpen(false)} disabled={bulkAckLoading}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleBulkTseAckConfirm}
                        color="warning"
                        variant="contained"
                        disabled={bulkAckLoading}
                    >
                        {bulkAckLoading ? "…" : "Quittieren"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                message={snackbar.message}
            />
            <Box sx={{ width: "100%" }}>
                <DataGrid
                    rows={licenses}
                    columns={columns}
                    pageSize={20}
                    rowsPerPageOptions={[20, 50, 100]}
                    checkboxSelection
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    components={{ Toolbar: GridToolbar }}
                    getRowId={(row) => row.LicenseId}
                    onCellEditCommit={handleUpdate}
                    treeData
                    getTreeDataPath={(row) => row.path}
                />
            </Box>{" "}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" color="primary" onClick={() => setIsAddDialogOpen(true)} sx={{ mt: 2 }}>
                    Lizenz hinzufügen
                </Button>
            </Box>
            <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
                <DialogTitle>Neue Lizenz hinzufügen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name"
                        fullWidth
                        variant="standard"
                        value={newLicense.StoreName}
                        onChange={(e) => setNewLicense({ ...newLicense, StoreName: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Inhaber / GF"
                        fullWidth
                        variant="standard"
                        value={newLicense.Owner}
                        onChange={(e) => setNewLicense({ ...newLicense, Owner: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Lizenzablauf"
                        type="date"
                        fullWidth
                        variant="standard"
                        value={newLicense.ValidityDate}
                        onChange={(e) => setNewLicense({ ...newLicense, ValidityDate: e.target.value })}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsAddDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleInsert}>Hinzufügen</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Licenses;
